import {
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { UserContext } from 'context/User/UserContext';
import { debounce } from 'utils/misc/debounce';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import PageWithBreadcrumbs from '../layouts/PageWithBreadcrumbs';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';
import { MSPerson } from 'components/Navigation/AppBar/MSPerson/MSPerson';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { addMessage, clearMessages } from 'store/ancillary/actions';
import {
	getEMP,
	getUserAppStatus,
	postReminderEmail,
	postTrainingFlip,
} from '../_api';
import { fetchDetailedStaffInformation } from 'pages/AnnualUserAccessReview/_api';
import {
	getDirectReport,
	getJobCategory,
	getTrainingTracks,
} from 'pages/Bridges/ManagerValidation/_api';
import { DirectReport } from 'pages/Bridges/ManagerValidation/_constants';
import { OBJ_APP_BRIDGES } from 'pages/Bridges/ManagerValidation/_constants';
import { AssignmentDetails, UserSearchResult } from '../_classes';

import classes from '../../../Bridges/ManagerValidation/components/ValidationTable.module.scss';

const DetailsView = ({ isAzureAuthorized, isRoleAuthorized }) => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const admin =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const { id } = useParams();
	const dispatch = useDispatch();
	const [sEmailSending, setEmailSending] = useState(false);
	const [sEmailSuccess, setEmailSuccess] = useState(false);
	const history = useHistory();
	const systemID = Number(
		history?.location?.state?.systemID ?? OBJ_APP_BRIDGES.ID,
	);
	const systemName =
		history?.location?.state?.systemName ?? OBJ_APP_BRIDGES.Name;
		
	/**
	 * @type {[UserSearchResult, Function]}
	 */
	const [sUser, setUser] = useState(null);
	const _border = '1px solid rgba(224, 224, 224, 1)';

	useEffect(() => {
		const render = async id => {
			dispatch(clearMessages());

			try {
				const objUserInfo = await fetchDetailedStaffInformation(id);
				const emp =
					systemID === OBJ_APP_BRIDGES.ID && objUserInfo?.upn
						? await getEMP(objUserInfo.upn)
						: null;

				const appStatus = await getUserAppStatus(id, systemID);
				const response = await getDirectReport(
					objUserInfo.supervisorFiId,
					id,
					systemID,
					true,
				);

				const arrReportAssignments = response?.data?.payload?.some(
					report => report?.Assignment?.ID > -1,
				)
					? response.data.payload.map(
							report => new DirectReport(report),
						)
					: [];
				let arrAssignments = [];

				for (let i = 0; i < arrReportAssignments.length; i++) {
					let result = new AssignmentDetails(
						arrReportAssignments[i].assignment,
					);
					result.trainingTracks = [];

					const jobCategory = await getJobCategory(
						result.jobCategory.id,
					);
					if (jobCategory?.trainingTracks?.length > 0) {
						result.trainingTracks = await getTrainingTracks(
							jobCategory.trainingTracks,
						);
					}

					arrAssignments.push(result);
				}

				const objUser = new UserSearchResult({
					ID: id,
					FirstName: objUserInfo.firstName,
					LastName: objUserInfo.lastName,
					MiddleInitial: objUserInfo.middleInitial?.replace(
						'NOMI',
						'',
					),
					Department: objUserInfo.departmentName,
					JobTitle: objUserInfo.jobTitle,
					ManagerEmail: objUserInfo.supervisorEmail,
					UPN: objUserInfo.upn,
					Assignments: arrAssignments,
					AppAccess:
						appStatus === null
							? 'No'
							: appStatus.pendingRequestItem !== null
								? `Pending (${appStatus.pendingRequestTrackingNumber})`
								: 'Yes',
					IsExists: emp !== null,
					IsActive: emp?.IsActive,
					BlockStatus: emp?.BlockStatus,
				});

				setUser(objUser);
			} catch {
				raiseErrorBanner(
					'An error occurred while fetching user data.',
					dispatch,
				);
			}
		};
		if (sUser === null || sUser.id !== id) {
			render(id);
		}
	}, [dispatch, sUser, systemID, id]);

	/**
	 * Memoized, debounced event handler on job category "trained" status change.
	 */
	const onCheckHandler = useMemo(() => {
		return debounce(async assignment => {
			dispatch(clearMessages());

			try {
				let userCopy = { ...sUser };
				assignment.trained = !assignment.trained;
				const response = await postTrainingFlip(
					admin,
					sUser.id,
					assignment.jobCategory.id,
					assignment.trained,
				);
				if (response?.data?.payload) {
					const indexAssignment = userCopy.assignments.findIndex(
						el => el.id === assignment.id,
					);
					userCopy.assignments[indexAssignment] = assignment;
				}
				if (response?.data?.messages) {
					response.data.messages.forEach(message =>
						dispatch(addMessage(message)),
					);
				}
				setUser(userCopy);
			} catch {
				raiseErrorBanner(
					'An error occurred while updating training state.',
					dispatch,
				);
			}
		}, 300);
	}, [dispatch, admin, sUser]);

	const onClickHandler = useCallback(async () => {
		dispatch(clearMessages());

		try {
			setEmailSending(true);
			const success = await postReminderEmail(admin, sUser.id);
			setEmailSuccess(success);
		} catch {
			raiseErrorBanner(
				'An error occurred while sending reminder email.',
				dispatch,
			);
		} finally {
			setTimeout(() => {
				setEmailSending(false);
				setEmailSuccess(false);
			}, 2000);
		}
	}, [dispatch, admin, sUser]);

	const elCard = sUser ? (
		<Card
			sx={{
				width: 'fit-content',
				alignSelf: 'end',
			}}
		>
			<CardHeader
				sx={{ paddingBottom: 'unset' }}
				avatar={
					<MSPerson
						person={{
							givenName: sUser.firstName,
							surname: sUser.lastName,
						}}
					/>
				}
				title={`${sUser.lastName}, ${sUser.firstName} ${sUser.middleInitial}`.trim()}
				subheader={sUser.jobTitle}
			/>
			<CardContent>
				<Typography variant='subtitle1'>{sUser.name}</Typography>
				<Table
					sx={{
						whiteSpace: 'nowrap',
					}}
				>
					<TableBody
						sx={{
							'& td': {
								borderBottom: 'unset',
							},
							'& p': { fontSize: '.8rem' },
						}}
					>
						<TableRow>
							<TableCell
								sx={{
									padding: '0px',
								}}
								size='small'
							>
								<Typography variant='body2'>
									Department:
								</Typography>
							</TableCell>
							<TableCell
								sx={{
									paddingTop: '0px',
									paddingBottom: '0px',
								}}
								size='small'
							>
								<Typography variant='body2'>
									{sUser.department?.toUpperCase()}
								</Typography>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell
								sx={{
									paddingLeft: '0px',
									paddingRight: '0px',
								}}
								size='small'
							>
								<Typography variant='body2'>UPN:</Typography>
							</TableCell>
							<TableCell
								sx={{ paddingTop: '0px', paddingBottom: '0px' }}
								size='small'
							>
								<Typography variant='body2'>
									{sUser.upn?.toLowerCase()}
								</Typography>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell
								sx={{
									padding: '0px',
								}}
								size='small'
							>
								<Typography variant='body2'>
									Manager Email:
								</Typography>
							</TableCell>
							<TableCell
								sx={{
									paddingTop: '0px',
									paddingBottom: '0px',
								}}
								size='small'
							>
								<Typography variant='body2'>
									{sUser.emailManager?.toLowerCase()}
									{sUser.assignments.some(
										assignment => !assignment.validated,
									) && (
										<Tooltip
											placement='right'
											title='Email Reminder'
										>
											<IconButton
												sx={{
													marginLeft: '8px',
												}}
												size='small'
												aria-label='Remind'
												color={'primary'}
												disabled={
													isAzureAuthorized ||
													sEmailSending
												}
												id='icon--email'
												onClick={onClickHandler}
											>
												{sEmailSending ? (
													sEmailSuccess ? (
														<CheckIcon
															fontSize='small'
															color='success'
														/>
													) : (
														<CircularProgress
															size={20}
														/>
													)
												) : (
													<EmailIcon fontSize='small' />
												)}
											</IconButton>
										</Tooltip>
									)}
								</Typography>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell
								sx={{
									paddingLeft: '0px',
									paddingRight: '0px',
									paddingBottom: 'unset',
								}}
								size='small'
							>
								<Typography variant='body2'>
									{`IMS Epic ${systemName} Access:`}
								</Typography>
							</TableCell>
							<TableCell
								sx={theme => {
									return {
										paddingBottom: 'unset',
										color:
											sUser.appAccess === 'Yes'
												? theme.palette.success.main
												: sUser.appAccess === 'No'
													? theme.palette.error.main
													: theme.palette.warning
															.main,
									};
								}}
								size='small'
							>
								<Typography variant='body2'>
									{sUser.appAccess}
								</Typography>
							</TableCell>
						</TableRow>
						{systemID === OBJ_APP_BRIDGES.ID && (
							<TableRow>
								<TableCell
									sx={{
										paddingLeft: '0px',
										paddingRight: '0px',
										paddingBottom: 'unset',
									}}
									size='small'
								>
									<Typography variant='body2'>
										Exists in Hyperspace:
									</Typography>
								</TableCell>
								<TableCell
									sx={theme => {
										return {
											paddingBottom: 'unset',
											color: sUser.empExists
												? sUser.empActive
													? theme.palette.success.main
													: theme.palette.error.main
												: theme.palette.error.main,
										};
									}}
									size='small'
								>
									<Typography variant='body2'>
										{sUser.empExists
											? `Yes (${sUser.empActive ? 'Active' : 'Inactive'})`
											: 'No'}
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{sUser.empBlock !== null && (
							<TableRow>
								<TableCell
									sx={{
										paddingLeft: '0px',
										paddingRight: '0px',
										paddingBottom: 'unset',
									}}
									size='small'
								>
									<Typography variant='body2'>
										Hyperspace Block:
									</Typography>
								</TableCell>
								<TableCell
									sx={theme => {
										return {
											paddingBottom: 'unset',
											color:
												sUser.empBlock === 'No'
													? theme.palette.success.main
													: theme.palette.error.main,
										};
									}}
									size='small'
								>
									<Typography variant='body2'>
										{sUser.empBlock}
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	) : null;

	const elTable = sUser ? (
		<>
			<Typography sx={{ margin: '8px 0' }} color='primary' variant='h5'>
				User Job Categories
			</Typography>
			<Table
				className={classes['table']}
				stickyHeader
				sx={{
					marginBottom: '16px',
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell
							sx={{ minWidth: '115px' }}
							align='center'
							variant='head'
						>
							Trained
							<Tooltip
								slotProps={{
									tooltip: {
										sx: {
											maxWidth: 'none',
										},
									},
								}}
								placement='bottom'
								title='Flip training block. Control enabled if the job category has been validated and requires training.'
							>
								<InfoIcon
									sx={{
										marginLeft: '4px',
										verticalAlign: 'middle',
										marginBottom: '3.5px',
									}}
									color='action'
									fontSize='small'
								/>
							</Tooltip>
						</TableCell>
						<TableCell variant='head'>Job Category</TableCell>
						<TableCell sx={{ minWidth: '100px' }} variant='head'>
							Validated
						</TableCell>
						<TableCell sx={{ minWidth: '100px' }} variant='head'>
							Experienced
						</TableCell>
						<TableCell variant='head'>Wave</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{sUser.assignments.map((assignment, index) => {
						const hasTT = assignment.trainingTracks.length > 0;
						const sxCellJC = {
							borderBottom:
								hasTT || index === sUser.assignments.length - 1
									? 'unset'
									: '',
							paddingBottom: hasTT ? '6px' : '16px',
						};
						const isEven = index % 2 === 0;

						return (
							<Fragment key={assignment.id}>
								<TableRow
									sx={theme => ({
										backgroundColor: isEven
											? theme.palette.action.hover
											: 'inherit',
									})}
								>
									<TableCell
										sx={{
											...sxCellJC,
											borderLeft: _border,
										}}
										align='center'
									>
										<FormControl>
											<Checkbox
												color='success'
												checked={
													assignment.trained === true
												}
												disabled={
													!hasTT ||
													!assignment.validated
												}
												onChange={() =>
													onCheckHandler(assignment)
												}
											/>
										</FormControl>
									</TableCell>
									<TableCell
										sx={{
											...sxCellJC,
											width: '60%',
											maxWidth: '60%',
										}}
									>
										<b>{assignment.jobCategory.name}</b>
									</TableCell>
									<TableCell
										sx={theme => {
											return {
												...sxCellJC,
												color:
													assignment.validated ===
													true
														? theme.palette.success
																.main
														: theme.palette.error
																.main,
											};
										}}
									>
										{assignment.validated ? 'Yes' : 'No'}
									</TableCell>
									<TableCell sx={{ ...sxCellJC }}>
										{assignment.experienced ? 'Yes' : 'No'}
									</TableCell>
									<TableCell
										sx={{
											...sxCellJC,
											borderRight: _border,
										}}
									>
										{assignment.wave ?? 3}
									</TableCell>
								</TableRow>
								{assignment.trainingTracks?.map(
									(track, index) => {
										const isLastTT =
											index ===
											assignment.trainingTracks.length -
												1;
										return (
											<TableRow
												sx={theme => ({
													backgroundColor: isEven
														? theme.palette.action
																.hover
														: 'inherit',
													'& td': {
														borderBottom: 'unset',
													},
												})}
											>
												<TableCell
													sx={{
														borderLeft: _border,
														borderBottom: !isLastTT
															? 'unset'
															: '',
													}}
													size='small'
												/>
												<TableCell
													sx={{
														borderRight: _border,
														paddingBottom: isLastTT
															? '16px'
															: 'inherit',
													}}
													colSpan={4}
													size='small'
												>
													<i>
														Training Track{' '}
														{index + 1}:
													</i>{' '}
													{track?.name}
												</TableCell>
											</TableRow>
										);
									},
								)}
							</Fragment>
						);
					})}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TablePagination
							sx={{
								borderTop: _border,
								borderLeft: _border,
								borderRight: _border,
								'& .MuiTablePagination-displayedRows': {
									marginBottom: '1.75px',
								},
							}}
							count={sUser.assignments.length}
							onPageChange={() => {}}
							page={0}
							rowsPerPage={20}
							rowsPerPageOptions={[]}
							showFirstButton={false}
							showLastButton={false}
						></TablePagination>
					</TableRow>
				</TableFooter>
			</Table>
		</>
	) : null;

	const onButtonClickedHandler = useCallback(
		() => history.push('/EpicUtilities/User/Search'),
		[history],
	);

	return sUser ? (
		<>
			{isRoleAuthorized && (
				<PageWithBreadcrumbs currentPageLabel={`${systemName} User`} />
			)}
			<Box
				sx={{
					width: '75%',
					margin: '0px auto 0px auto',
					marginTop: !isRoleAuthorized ? '-10px' : '-25px',
					paddingTop: '16px',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{elCard}
				{elTable}
			</Box>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					paddingBottom: '16px',
				}}
			>
				<Button
					variant='contained'
					onClick={onButtonClickedHandler}
					startIcon={<SearchIcon />}
				>
					Search For Different User
				</Button>
			</Box>
		</>
	) : (
		<WithLoadingHandler isLoading />
	);
};

export default DetailsView;
